// ─── Types ────────────────────────────────────────────────────────────────────

interface OlbEventParams {
    marketplace_id?: number;
    marketplace_slug?: string;
    [key: string]: unknown;
}

export interface OlbPurchaseItem {
    item_id: number;
    item_name: string;
    item_variant: string;
    item_category: string;
    price: number;
    quantity: 1;
}

// ─── Business area ────────────────────────────────────────────────────────────

/**
 * Derives the business area(s) from booking items, preferring the selected
 * service's category (e.g. distinguishes dental from grooming within the
 * grooming flow). Mixed bookings are joined: 'dental,grooming'.
 */
export function getBusinessArea(
    items: Array<{ businessArea: string; service?: { businessArea: string } }>
): string | undefined {
    const areas = Array.from(
        new Set(items.map(item => item.service?.businessArea ?? item.businessArea))
    );

    return areas.length > 0 ? areas.sort().join(',') : undefined;
}

// ─── Base ─────────────────────────────────────────────────────────────────────

export function trackEvent(eventName: string, params: OlbEventParams = {}): void {
    try {
        if (window.gtag) {
            window.gtag('event', eventName, params);
        } else if (window.dataLayer) {
            window.dataLayer.push({ event: eventName, ...params });
        }
    } catch {
        // Analytics errors must never interrupt the booking flow
    }
}

// ─── Funnel ───────────────────────────────────────────────────────────────────

export function trackStepView(params: {
    step_name: string;
    marketplace_id: number;
    marketplace_slug: string;
    business_area?: string;
    is_returning_client: boolean;
}): void {
    trackEvent('olb_step_view', params);
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export function trackSignUp(marketplaceSlug: string): void {
    trackEvent('sign_up', { method: 'online_booking', marketplace_slug: marketplaceSlug });
}

export function trackLogin(marketplaceSlug: string): void {
    trackEvent('login', { method: 'sms_code', marketplace_slug: marketplaceSlug });
}

// ─── Booking flow ─────────────────────────────────────────────────────────────

export function trackServiceSelected(params: {
    marketplace_slug: string;
    business_area?: string;
    service_names: string;
    pets_count: number;
}): void {
    trackEvent('olb_service_selected', params);
}

export function trackStaffSelected(params: {
    marketplace_slug: string;
    business_area?: string;
    staff_preference: 'specific' | 'no_preference';
}): void {
    trackEvent('olb_staff_selected', params);
}

export function trackDateTimeSelected(params: {
    marketplace_slug: string;
    business_area?: string;
    booking_date: string;
    time_of_day: 'morning' | 'afternoon';
}): void {
    trackEvent('olb_datetime_selected', params);
}

// ─── Conversions ──────────────────────────────────────────────────────────────

export function trackBookingCompleted(params: {
    transaction_id: string;
    marketplace_id: number;
    marketplace_slug: string;
    business_area?: string;
    deposit_value: number;
    items: OlbPurchaseItem[];
}): void {
    trackEvent('purchase', {
        transaction_id: params.transaction_id,
        value: params.deposit_value,
        currency: 'USD',
        marketplace_id: params.marketplace_id,
        marketplace_slug: params.marketplace_slug,
        business_area: params.business_area,
        items: params.items
    });
}

export function trackBookingRequested(params: {
    transaction_id: string;
    marketplace_id: number;
    marketplace_slug: string;
    business_area?: string;
    services_count: number;
}): void {
    trackEvent('olb_booking_requested', params);
}

// ─── Injection ────────────────────────────────────────────────────────────────

const GTM_SCRIPT_ID = 'cuddles-gtm-script';
const GTM_NOSCRIPT_ID = 'cuddles-gtm-noscript';
const GA4_LOADER_SCRIPT_ID = 'cuddles-ga4-loader';
const GA4_INIT_SCRIPT_ID = 'cuddles-ga4-init';

export function injectGTM(gtmId: string): void {
    if (document.getElementById(GTM_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = GTM_SCRIPT_ID;
    script.innerHTML = [
        'window.dataLayer = window.dataLayer || [];',
        '(function(w,d,s,l,i){',
        '  w[l]=w[l]||[];',
        "  w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});",
        '  var f=d.getElementsByTagName(s)[0],',
        '      j=d.createElement(s),',
        "      dl=l!='dataLayer'?'&l='+l:'';",
        '  j.async=true;',
        "  j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;",
        '  f.parentNode.insertBefore(j,f);',
        `})(window,document,'script','dataLayer','${gtmId}');`
    ].join('\n');
    document.head.appendChild(script);

    if (!document.getElementById(GTM_NOSCRIPT_ID)) {
        const noscript = document.createElement('noscript');
        noscript.id = GTM_NOSCRIPT_ID;
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(noscript, document.body.firstChild);
    }
}

export function injectGA4(gaId: string): void {
    if (document.getElementById(GA4_LOADER_SCRIPT_ID)) return;

    const loader = document.createElement('script');
    loader.id = GA4_LOADER_SCRIPT_ID;
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(loader);

    if (!document.getElementById(GA4_INIT_SCRIPT_ID)) {
        const init = document.createElement('script');
        init.id = GA4_INIT_SCRIPT_ID;
        init.innerHTML = [
            'window.dataLayer = window.dataLayer || [];',
            'function gtag(){dataLayer.push(arguments);}',
            "gtag('js', new Date());",
            `gtag('config', '${gaId}');`
        ].join('\n');
        document.head.appendChild(init);
    }
}
